import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';

import { ICategory } from '@/models';

import Sorting from './Sorting';
import GenericFilters from './GenericFilters';
import ColorFilter from './ColorFilter';
import SizeFilter from './SizeFilter';
import AppliedFilters from './AppliedFilters';
import useQueryParams from '@/hooks/useQueryParams';
import { buttonVariants } from '@/components/v2/UI/Button';
import { AdjustmentsIcon, XIcon } from '@heroicons/react/solid';
import RelatedCategoriesFilter from './RelatedCategoriesFilters';

interface Props {
  category: ICategory;
  count?: number;
}

const DesktopFilters: React.FC<Props> = ({ category }) => {
  const { t } = useTranslation();
  return (
    <aside className="top-0 flex-col hidden divide-y divide-gray-200 h-min xl:flex xl:sticky">
      <AppliedFilters category={category} />

      {category.filters?.relatedSports && (
        <RelatedCategoriesFilter
          relatedCategories={category.filters.relatedSports}
          label={'sport'}
        />
      )}
      {category.filters?.relatedCategories && (
        <RelatedCategoriesFilter
          relatedCategories={category.filters.relatedCategories}
          label={'category'}
        />
      )}
      <SizeFilter category={category} />
      <ColorFilter category={category} />
      <GenericFilters category={category} />
    </aside>
  );
};

const MobileFilters: React.FC<Props> = ({ category, count }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useQueryParams();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  function toggleMobileFilters() {
    setMobileFiltersOpen(mobileFiltersOpen => !mobileFiltersOpen);
  }

  function removeAllFilters() {
    setQuery(
      Object.keys(query).reduce(
        (acc, key) => ({ ...acc, [key]: undefined }),
        {} as Record<string, undefined>
      )
    );
  }
  return (
    <div className="xl:hidden">
      <button
        onClick={toggleMobileFilters}
        className={buttonVariants({
          variant: 'light',
          className: 'flex items-center space-x-1 xl:hidden'
        })}
      >
        <span className="text-xs">{t('P_PLP.FILTERS.TITLE')}</span>
        <AdjustmentsIcon className="w-5 h-5" />
      </button>

      <Dialog open={mobileFiltersOpen} onClose={() => {}}>
        <Dialog.Panel className="fixed inset-0 overflow-y-auto bg-white">
          <div className="flex items-center justify-between p-6 xl:hidden">
            <h3 className="text-lg font-bold tracking-wide uppercase">
              {t('P_PLP.FILTERS.FILTER_BY')}
            </h3>

            <div className="flex items-center space-x-6">
              <button
                onClick={removeAllFilters}
                className="text-sm tracking-wide underline underline-offset-2"
              >
                {t('P_PLP.FILTERS.REMOVE_ALL')}
              </button>

              <button onClick={toggleMobileFilters}>
                <XIcon className="w-8 h-8" />
              </button>
            </div>
          </div>

          <Sorting />
          {category.filters?.relatedSports && (
            <RelatedCategoriesFilter
              relatedCategories={category.filters.relatedSports}
              label={'sport'}
            />
          )}
          {category.filters?.relatedCategories && (
            <RelatedCategoriesFilter
              relatedCategories={category.filters.relatedCategories}
              label={'category'}
            />
          )}
          <ColorFilter category={category} />
          <SizeFilter category={category} />
          <GenericFilters category={category} />

          <div className="p-6">
            <button
              onClick={toggleMobileFilters}
              className={twMerge(
                buttonVariants({
                  variant: 'dark'
                }),
                'w-full'
              )}
            >
              {t('P_PLP.FILTERS.APPLY')} ({count})
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
};

export { DesktopFilters, MobileFilters };
